import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "../Hooks/useInView";
import styles from "./CompanyDescription.module.css";
import CustomButton from "../components/CustomButton";

const CLIP: "IMG_1002" = "IMG_1002";

const LINE1 = "Wir gestalten Lebensräume mit Weitblick.";
const LINE2 = "Wir entwickeln Zukunft.";
const LINE3 = "Gemeinsam. Verlässlich.";

const srcOf = (n: string, level: "720" | "1080", ext: "webm" | "mp4") =>
  `/media/video-light/about/${level}/${n}-${level}.${ext}`;
const posterOf = (n: string) => `/media/video-light/about/posters/${n}.jpg`;

const MOBILE_FOCAL: Record<string, string> = { IMG_1002: "50% 40%" };
const DESKTOP_FOCAL: Record<string, string> = { IMG_1002: "50% 50%" };


const paragraphs = [
  `Seit 2003 entwickelt und steuert die AVILA Projektmanagement GmbH neue Immobilien- sowie Sanierungsprojekte für die AVILA-Gruppe.`,
  "Als erfahrene Projektentwickler und Projektsteuerer mit einem klaren Fokus auf Qualität, Wirtschaftlichkeit und Nachhaltigkeit schaffen wir Lebens- und Arbeitsräume, die überzeugen – architektonisch, funktional und wirtschaftlich.",
  "Von der ersten Konzeptidee bis zur schlüsselfertigen Immobilie begleiten wir den Bauherrn im gesamten Entwicklungsprozess seiner Bau- und Immobilienprojekte und sind auf die Entwicklung und Umsetzung komplexer Bauvorhaben spezialisiert. Thematische Schwerpunkte liegen dabei im anspruchsvollen Wohnungsbau mit den Spezialkompetenzen Studentenwohnen und Wohnen für ältere Menschen mit Betreuung und Pflege.",
  "Wir haben immer den Anspruch vor Augen, funktionale und lebenswerte Räume zu schaffen. Wir entwickeln neue Wohnquartiere, bauen und sanieren im Bestand sowie denkmalgeschützte Objekte und realisieren darüber hinaus Hotels und Resorts. Jeder Standort, jede Immobilie und jede Zielgruppe ist einzigartig – deshalb entwickeln wir individuelle Lösungen, die dauerhaft überzeugen.",
  "In den letzten Jahren wurden in diesen Segmenten mehr als 2.100 Wohneinheiten auf einer Wohn- und Gewerbefläche von 149.000 m² neu gebaut. Hinzu kommen Komplettsanierungen im bewohnten Bestand von über 2.500 Wohnungen in den Jahren 2005 bis 2008.",
  "Unser Anspruch ist es, aus Visionen realisierbare Konzepte zu formen und diese effizient, kostenbewusst und termingerecht umzusetzen. Dabei verbinden wir technisches Know-how mit strategischem Denken, architektonischer Klarheit und einer partnerschaftlichen Arbeitsweise.",
  "Ob Neubau, Sanierung oder Quartiersentwicklung – wir bringen Projekte auf den Weg und sorgen dafür, dass sie sicher ans Ziel kommen."
];

const highlights = [
  "Ganzheitliche Projektentwicklung mit Blick für Details",
  "Strukturierte und transparente Projektsteuerung",
  "Individuelle Lösungen für komplexe Herausforderungen",
  "Verlässlichkeit, Engagement und ein starkes Netzwerk",
];


const isSplashActive = () =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-splash") === "1";

const CompanyDescription: React.FC = () => {
  const [ref, inView] = useInView<HTMLDivElement>(0.1);

  const [isWide] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1200px)").matches;
  });

  const saveData = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    // @ts-ignore experimental
    return navigator.connection?.saveData === true;
  }, []);

  const motionReduced = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const level: "720" | "1080" = useMemo(
    () => (isWide && !saveData ? "1080" : "720"),
    [isWide, saveData]
  );

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(max-width: 820px)").matches ||
      window.matchMedia("(pointer: coarse)").matches
    );
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqW = window.matchMedia("(max-width: 820px)");
    const mqT = window.matchMedia("(pointer: coarse)");
    const apply = () => setIsMobile(mqW.matches || mqT.matches);
    mqW.addEventListener?.("change", apply);
    mqT.addEventListener?.("change", apply);
    return () => {
      mqW.removeEventListener?.("change", apply);
      mqT.removeEventListener?.("change", apply);
    };
  }, []);

  const focalFor = useCallback(
    (name: string) =>
      (isMobile ? MOBILE_FOCAL : DESKTOP_FOCAL)[name] ?? "50% 50%",
    [isMobile]
  );

  const vRef = useRef<HTMLVideoElement>(null);
  const [objPos, setObjPos] = useState("50% 50%");
  const [posterVisible, setPosterVisible] = useState(true);


  const startedOnce = useRef(false);
  const readyOnce = useRef(false);

  useEffect(() => setObjPos(focalFor(CLIP)), [focalFor]);

  useEffect(() => {
    const v = vRef.current;
    if (!v) return;
    v.muted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
  }, []);

  const markReady = useCallback(() => {
    if (readyOnce.current) return;
    readyOnce.current = true;
    setPosterVisible(false);
  }, []);

  const waitFirstFramePresented = useCallback(
    (v: HTMLVideoElement) => {
      const anyV = v as any;

      if (typeof anyV.requestVideoFrameCallback === "function") {
        anyV.requestVideoFrameCallback(() => markReady());
        return;
      }

      const on = () => {
        if (v.videoWidth > 0) markReady();
      };
      v.addEventListener("loadeddata", on, { once: true });
    },
    [markReady]
  );

  const startVideo = useCallback(async () => {
    const v = vRef.current;
    if (!v || startedOnce.current) return;

    startedOnce.current = true;

    setPosterVisible(true);
    readyOnce.current = false;

    try {
      v.muted = true;
      v.playsInline = true;
      v.loop = true;

      try {
        v.currentTime = 0;
      } catch {
      }

      const p = v.play();
      if (p?.catch) p.catch(() => {});

      waitFirstFramePresented(v);
    } catch {
      startedOnce.current = false;
      setPosterVisible(true);
    }
  }, [waitFirstFramePresented]);

  useEffect(() => {

    if (motionReduced || saveData) {
      setPosterVisible(true);
      return;
    }

    startedOnce.current = false;
    readyOnce.current = false;
    setPosterVisible(true);

    if (!isSplashActive()) {
      startVideo();
      return;
    }

    const root = document.documentElement;
    const obs = new MutationObserver(() => {
      if (!isSplashActive()) {
        obs.disconnect();
        startVideo();
      }
    });

    obs.observe(root, { attributes: true, attributeFilter: ["data-splash"] });
    return () => obs.disconnect();
  }, [motionReduced, saveData, startVideo]);

  useEffect(() => {
    const v = vRef.current;
    if (!v) return;

    const onErr = () => setPosterVisible(true);
    v.addEventListener("error", onErr);

    const t = window.setTimeout(() => {
      if (!readyOnce.current) setPosterVisible(true);
    }, 6000);

    return () => {
      v.removeEventListener("error", onErr);
      window.clearTimeout(t);
    };
  }, []);

  const [expanded, setExpanded] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 1024;
  });

  return (
    <>
      <div className={styles.heroStage}>
        {!motionReduced && !saveData && (
          <video
            ref={vRef}
            className={styles.heroVideo}
            style={{ objectPosition: objPos }}
            muted
            loop
            playsInline
            preload="metadata"
            disableRemotePlayback
          >
            <source
              src={srcOf(CLIP, level, "webm")}
              type='video/webm; codecs="vp9"'
            />
            <source src={srcOf(CLIP, level, "mp4")} type="video/mp4" />
          </video>
        )}

        <img
          className={`${styles.heroPoster} ${
            posterVisible ? "" : styles.posterHidden
          }`}
          src={posterOf(CLIP)}
          alt=""
          aria-hidden="true"
          style={{ objectPosition: objPos }}
        />

        <div className={styles.heroOverlay} aria-hidden="true" />
        <div className={styles.heroContent}>
          <h1
            className={styles.heroTitle}
            aria-label={`${LINE1} ${LINE2} ${LINE3}`}
          >
            <span className={`${styles.line} ${styles.line1}`}>{LINE1}</span>
            <span className={`${styles.line} ${styles.line2}`}>{LINE2}</span>
            <span className={`${styles.line} ${styles.line3}`}>{LINE3}</span>
          </h1>
        </div>
      </div>

      <section className={styles.aboutIntro} ref={ref}>
        <h2 className={styles.sectionTitle}>
          AVILA<span className={styles.subLabel}> Projektmanagement GmbH</span>
        </h2>

        <div
          className={`${styles.container} ${styles.fadeInSection} ${
            inView ? styles.visible : ""
          }`}
        >
          {paragraphs
            .slice(0, expanded ? paragraphs.length : 3)
            .map((text, idx) => (
              <p key={idx}>{text}</p>
            ))}

          {!expanded &&
            !(typeof window !== "undefined" && window.innerWidth >= 1024) && (
              <div className={styles.buttonWrapper}>
                <CustomButton
                  text="Mehr lesen"
                  onClick={() => setExpanded(true)}
                  variant="filled"
                  size="medium"
                />
              </div>
            )}

          {expanded && (
            <>
              <h3 className={styles.subTitle}>Was uns auszeichnet:</h3>
              <ul className={styles.highlights}>
                {highlights.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default CompanyDescription;